/**
 * SiteSafetyDocDialog — admin editor for a site's safety frame:
 *   · Risk profile (long-form)
 *   · Method-statement framework (long-form, reused across jobs)
 *   · Access rules / restrictions
 *   · Known hazards (chip list)
 *
 * Exposed via the "Safety doc" pill on every site row. Saves through
 * `PATCH /api/sites/{id}/safety-doc` and feeds the AI RAMS generator
 * for every WO on this site.
 */
import React, { useState, useEffect } from 'react';
import { ShieldAlert, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { updateSiteSafetyDoc } from '../services/api';

export const SiteSafetyDocDialog = ({ site, open, onOpenChange, onSaved }) => {
  const [riskProfile, setRiskProfile] = useState('');
  const [methodFramework, setMethodFramework] = useState('');
  const [accessRules, setAccessRules] = useState('');
  const [hazards, setHazards] = useState([]);
  const [hazardDraft, setHazardDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Re-hydrate whenever the dialog opens for a NEW site or the same
    // site is reopened. Tracking by `site.id` (not the object reference)
    // means a parent that swaps the prop without flipping `open` still
    // gets a clean hydration — defence-in-depth against stale state
    // leaking from one site into another's save payload.
    if (open && site) {
      setRiskProfile(site.riskProfile || '');
      setMethodFramework(site.methodFramework || '');
      setAccessRules(site.accessRules || '');
      setHazards(site.knownHazards || []);
      setHazardDraft('');
    }
  }, [open, site?.id, site?.riskProfile, site?.methodFramework, site?.accessRules, site?.knownHazards]);

  const addHazard = () => {
    const h = hazardDraft.trim();
    if (!h) return;
    if (!hazards.includes(h)) setHazards([...hazards, h]);
    setHazardDraft('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await updateSiteSafetyDoc(site.id, {
        riskProfile, methodFramework, accessRules, knownHazards: hazards,
      });
      toast.success('Site safety doc saved — will be used in next RAMS generation');
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!site) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="site-safety-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> {site.name} — Safety doc
          </DialogTitle>
          <DialogDescription>
            One-time setup per site. The AI RAMS generator pulls every field below into every work-order RAMS for this site.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Site risk profile</Label>
            <textarea
              rows={4}
              value={riskProfile}
              onChange={(e) => setRiskProfile(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="High-level overview of permanent risks at this site (production environment, traffic routes, hazardous substances stored on-site, etc.)"
              data-testid="site-safety-risk"
            />
          </div>
          <div>
            <Label>Site method-statement framework</Label>
            <textarea
              rows={4}
              value={methodFramework}
              onChange={(e) => setMethodFramework(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="Standing rules: who to report to on arrival, where the welfare facilities are, who the responsible person is, hand-back procedure, etc."
              data-testid="site-safety-method"
            />
          </div>
          <div>
            <Label>Access rules / restrictions</Label>
            <textarea
              rows={3}
              value={accessRules}
              onChange={(e) => setAccessRules(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-200"
              placeholder="Site induction required, escort needed, vehicle restrictions, opening hours, no-go zones, etc."
              data-testid="site-safety-access"
            />
          </div>
          <div>
            <Label>Known hazards</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={hazardDraft}
                onChange={(e) => setHazardDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHazard(); } }}
                placeholder="e.g. Live 11 kV switchgear in the same room"
                data-testid="site-safety-hazard-input"
              />
              <Button type="button" variant="outline" onClick={addHazard}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
            {hazards.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {hazards.map((h) => (
                  <span key={h} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800 text-xs">
                    {h}
                    <button type="button" onClick={() => setHazards(hazards.filter((x) => x !== h))} className="text-amber-600 hover:text-amber-900" aria-label={`Remove ${h}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="site-safety-save">
            {saving ? 'Saving…' : 'Save safety doc'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SiteSafetyDocDialog;
